import { BlockStack, Form, FormLayout, Modal, TextField, Text, SkeletonBodyText, Badge } from '@shopify/polaris';
import { ConfettiIcon } from '@shopify/polaris-icons';
import { useField, useForm } from '@shopify/react-form';
import __helpers from 'helpers/index';
import __ from 'languages/index';
import { useGetTransaction, useMutateUpdateTransaction } from 'queries/transactions.query';
import { useCallback, useEffect, useMemo } from 'react';
import dateandtime from 'date-and-time';
import { useMutateGetOrderViaCode } from 'queries/orders.query';
import helpers from 'helpers/index';
import { useNotification } from 'context/NotificationContext';

/** Hiển thị modal để assign đơn hàng vào trong transaction, trong trường hợp người dùng chuyển khoản nhầm nội dung thì vẫn pick được nó ra mà assign, không thể trả lại tiền được  */
export default function TransactionModal({ currentTransaction, show, onClose }: { currentTransaction: string; show: boolean; onClose: () => void }) {
  const { data: entity, isLoading } = useGetTransaction(currentTransaction);
  const { mutateAsync: updateTransactions, isPending: updating } = useMutateUpdateTransaction();
  const { addNotification } = useNotification();
  const {
    mutateAsync: getOrderData,
    data: orderData,
    reset: resetOrderData,
    isPending: loadingOrderViaCode,
    isError: loadingOrderDataError,
  } = useMutateGetOrderViaCode();

  const useFields = {
    /** create new one */
    order_id: useField<string>({ value: entity?.order_id, validates: [] }),
    order_code: useField<string>({ value: entity?.order_code, validates: [] }),
    order_code_inputtext: useField<string>({ value: '', validates: [] }),
    description: useField<string>({ value: entity?.description, validates: [] }),
    note: useField<string>({ value: entity?.note, validates: [] }),
  };

  const { fields, submit, submitting } = useForm({
    fields: useFields,
    async onSubmit(values) {
      try {
        await updateTransactions({
          id: currentTransaction,
          order_id: values.order_id,
          order_code: values.order_code,
          description: values.description,
          note: values.note,
        });
      } catch (e: any) {
        if (e.params !== undefined) {
          if (e.params.field !== undefined) {
            useFields[e.params.field].setError(__('check_this_field_again'));
          }
        }
        addNotification('error', e.message);
      }
      return { status: 'success', errors: [] };
    },
  });

  const fromClipboard = useCallback(async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      fields.order_id.onChange(clipboardText);
    } catch (e) {}
  }, []);

  /** Truy vấn tới server */
  const getOrderDataCallback = useCallback(async (order_code_inputtext) => {
    try {
      await getOrderData(order_code_inputtext);
    } catch (e) {}
  }, []);

  const debouncedFetch = useMemo(
    () =>
      helpers.debounce((order_code_inputtext: any) => {
        getOrderDataCallback(order_code_inputtext); // hoặc bỏ hẳn refetch, gọi API trực tiếp nếu được
      }, 1000),
    []
  );

  useEffect(() => {
    if ((useFields.order_code_inputtext.value || ' ').length > 2) debouncedFetch(useFields.order_code_inputtext.value);
  }, [useFields.order_code_inputtext.value, debouncedFetch]);

  /** Load được dữ liệu ra thì .... */
  useEffect(() => {
    if (orderData) {
      fields.order_id.onChange(orderData?.id);
      fields.order_code.onChange(orderData?.order_code);
    }
  }, [orderData]);

  /** trước khi off cái modal, thì ... thì ... */
  useEffect(() => {
    if (!show) {
      resetOrderData();
      helpers.resetForm(useFields);
    }
  }, [show]);

  return (
    <>
      <Modal
        open={show}
        title={`Hiệu chỉnh một giao dịch`}
        onClose={() => onClose()}
        primaryAction={{
          content: `Cập nhật`,
          icon: ConfettiIcon,
          disabled: isLoading,
          loading: submitting,
          onAction: submit,
        }}
      >
        <Modal.Section>
          <Form onSubmit={submit}>
            <BlockStack gap="400">
              <FormLayout.Group>
                <TextField autoComplete="off" readOnly value={__helpers.formatNumber(entity?.amount)} label={'Số tiền giao dịch'} />
                <TextField autoComplete="off" readOnly value={entity?.payment_method} label={'Loại giao dịch'} />
              </FormLayout.Group>
              <FormLayout.Group>
                <TextField autoComplete="off" readOnly value={entity?.casher_data?.display_name || 'Chưa rõ'} label={'Người thu tiền'} />
                <TextField
                  autoComplete="off"
                  readOnly
                  value={dateandtime.format(new Date(Number(entity?.createdAt)), 'HH:mm DD/MM/YYYY')}
                  label={'Ngày ghi nhận'}
                />
              </FormLayout.Group>
              <TextField
                label="Mã đơn hàng"
                autoComplete="off"
                disabled={submitting}
                clearButton
                value={fields.order_code_inputtext.value}
                labelAction={{
                  content: 'Dán',
                  onAction: () => {
                    fromClipboard();
                  },
                }}
                onChange={(v) => {
                  fields.order_code_inputtext.onChange(String(v));
                }}
                error={fields.order_code_inputtext.error}
                onClearButtonClick={() => {
                  fields.order_code_inputtext.onChange('');
                }}
              />

              <BlockStack gap="400">
                {loadingOrderViaCode ? <SkeletonBodyText /> : null}
                {orderData ? (
                  <>
                    <Text as="p" tone="success">
                      Đơn {orderData?.order_code} của khách hàng {orderData?.customer_data?.display_name} /{' '}
                      {orderData?.customer_data?.user_phonenumber} <br />
                      Đi từ: {orderData?.pickup_address}
                      <br />
                      Điểm đến: {orderData?.dropoff_address}
                      <br />
                      Phải thanh toán:{' '}
                      <Text as="span" fontWeight="bold" variant="headingMd">
                        {helpers.formatNumber(orderData?.total_must_pay)} VND
                      </Text>
                      <br />
                      {orderData?.transactions ? <Badge tone="success-strong">Đã thanh toán</Badge> : <Badge tone="critical">Chưa thanh toán</Badge>}
                    </Text>
                  </>
                ) : null}
                {loadingOrderDataError && (
                  <Text as="p" variant="headingMd" tone="critical" fontWeight="bold">
                    Không tìm thấy dữ liệu cho vé {fields.order_code_inputtext.value}
                  </Text>
                )}
              </BlockStack>

              <TextField
                autoComplete="off"
                placeholder="Nội dung giao dịch"
                label="Nội dung giao dịch"
                multiline={2}
                disabled={submitting}
                helpText={
                  'Cố gắng không chỉnh sửa nội dung giao dịch gốc khi khách chuyển khoản qua mã QR, vì nó liên quan tới báo cáo thuế. Nếu cần thì hãy ghi vào ghi chú.'
                }
                {...fields.description}
              />
              <TextField autoComplete="off" placeholder="Ghi chú" label="Ghi chú" multiline={2} disabled={submitting} {...fields.note} />
            </BlockStack>
          </Form>
        </Modal.Section>
      </Modal>
    </>
  );
}

